import { ANALYSIS_STATUS } from "@services/document-analysis";

/** Mensaje apto para el usuario a partir de un rechazo de axios. */
export const getUploadErrorMessage = (error) => {
    const data = error && error.response && error.response.data;
    if (typeof data === "string" && data) {
        return data;
    }
    if (error && error.code === "ECONNABORTED") {
        return "El análisis tardó demasiado. Intenta de nuevo.";
    }
    return "No se pudo subir el documento. Intenta de nuevo.";
};

/** Los requeridos sin subir. Un documento cuyo analisis fallo cuenta como subido: el archivo quedo guardado. */
export const getMissingRequired = (requirements, entries) =>
    requirements.filter((r) => r.required && !(entries[r.type] && entries[r.type].analysis));

/** Analisis que se entregan al flujo (y se ligan al caso), sin importar si el OCR logro leerlos. */
export const collectAnalyses = (requirements, entries) =>
    requirements
        .map((r) => entries[r.type] && entries[r.type].analysis)
        .filter(Boolean);

export const hasReadableData = (analysis) =>
    !!analysis && analysis.status === ANALYSIS_STATUS.COMPLETED;

const TRUST_WARNINGS = {
    apocrifo: "Parece un documento apócrifo",
    alterado: "Parece un documento alterado",
};

/**
 * Aviso para el usuario a partir del juicio del modelo (campos comunes a todos los tipos:
 * documento_reconocido, confiabilidad, motivo_confiabilidad). Es orientativo y no bloquea el
 * flujo. Devuelve null si no hay nada que avisar.
 */
export const getAnalysisWarning = (analysis) => {
    if (!hasReadableData(analysis) || !analysis.result) {
        return null;
    }
    const { documento_reconocido: recognized, confiabilidad, motivo_confiabilidad: reason } = analysis.result;
    const parts = [];
    if (recognized === false) {
        parts.push("No parece ser el documento solicitado");
    } else if (TRUST_WARNINGS[confiabilidad]) {
        parts.push(TRUST_WARNINGS[confiabilidad]);
    } else if (confiabilidad === "parece_valido") {
        parts.push("El documento tiene detalles por revisar");
    }
    if (parts.length === 0) {
        return null;
    }
    return reason ? `${parts[0]}: ${reason}` : parts[0];
};

const EXTENSIONS_BY_MIME = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
};

/** Extensiones (para el atributo accept) de los tipos MIME que acepta un documento. */
export const getAcceptedExtensions = (mimeTypes = []) =>
    mimeTypes.reduce((acc, mime) => acc.concat(EXTENSIONS_BY_MIME[mime] || []), []);

/**
 * Revision previa en el navegador para no subir un archivo que el backend va a rechazar. El
 * backend sigue validando por contenido; esto solo ahorra la espera. `type` es
 * { mimeTypes, maxMb } del endpoint de tipos; sin el, solo se revisa que haya archivo.
 * Devuelve el mensaje de error o null si el archivo es aceptable.
 */
export const validateFile = (file, type) => {
    if (!file) {
        return "Selecciona un archivo.";
    }
    if (type && type.mimeTypes && type.mimeTypes.length > 0) {
        const name = (file.name || "").toLowerCase();
        const extensionOk = getAcceptedExtensions(type.mimeTypes).some((ext) => name.endsWith(ext));
        const mimeOk = type.mimeTypes.includes(file.type);
        if (!extensionOk && !mimeOk) {
            const allowed = getAcceptedExtensions(type.mimeTypes)
                .map((ext) => ext.slice(1).toUpperCase())
                .filter((ext, index, all) => all.indexOf(ext) === index && ext !== "JPEG");
            return `Formato no permitido. Usa ${allowed.join(", ")}.`;
        }
    }
    if (type && type.maxMb && file.size > type.maxMb * 1024 * 1024) {
        return `El archivo excede ${type.maxMb} MB.`;
    }
    if (file.size === 0) {
        return "El archivo está vacío.";
    }
    return null;
};

const isFilled = (value) => value !== null && value !== undefined && String(value).trim() !== "";

/**
 * Cuantos datos principales leyó el modelo (valores no vacíos dentro de `datos`, a cualquier
 * profundidad). Es lo que se le muestra al usuario como "N datos reconocidos".
 */
export const countRecognizedFields = (analysis) => {
    if (!hasReadableData(analysis) || !analysis.result) {
        return 0;
    }
    const count = (node) => {
        if (node === null || node === undefined) {
            return 0;
        }
        if (typeof node === "object") {
            return Object.values(node).reduce((sum, child) => sum + count(child), 0);
        }
        return isFilled(node) ? 1 : 0;
    };
    return count(analysis.result.datos);
};

export const VERDICT_LEVEL = { OK: "ok", NOTE: "note", REVIEW: "review" };

/**
 * Resultado de validar la estructura de un documento, para el apartado de validaciones: "ok" (cuenta
 * con las caracteristicas correspondientes), "note" (las tiene, con detalles menores) o "review"
 * (hay que identificarlo para que AXA lo revise). Es informativo: no bloquea nada.
 */
export const getDocumentVerdict = (analysis) => {
    if (!hasReadableData(analysis) || !analysis.result) {
        return { level: VERDICT_LEVEL.REVIEW, message: "No se pudo leer el documento: AXA debe revisarlo." };
    }
    const { documento_reconocido: recognized, confiabilidad, motivo_confiabilidad: reason } = analysis.result;
    const withReason = (text) => (reason ? `${text}: ${reason}` : text);

    if (recognized === false) {
        return { level: VERDICT_LEVEL.REVIEW, message: "El documento no cuenta con la estructura solicitada." };
    }
    if (confiabilidad === "apocrifo" || confiabilidad === "alterado") {
        return { level: VERDICT_LEVEL.REVIEW, message: withReason(TRUST_WARNINGS[confiabilidad]) };
    }
    if (confiabilidad === "no_evaluable") {
        return { level: VERDICT_LEVEL.REVIEW, message: withReason("No se pudo evaluar el documento") };
    }
    if (confiabilidad === "parece_valido") {
        return {
            level: VERDICT_LEVEL.NOTE,
            message: withReason("Cuenta con las características correspondientes, con detalles por revisar"),
        };
    }
    return { level: VERDICT_LEVEL.OK, message: "El documento cuenta con las características correspondientes." };
};
